import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { t } from "@/i18n";
import { toast } from "sonner";
import { X, Plus, Gift as GiftIcon, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { AdeCoinIcon } from "./AdeCoinIcon";

import { CinematicGiftAnimation } from "./CinematicGiftAnimation";
import { getTier } from "./uncover/tiers";
import "./uncover/uncover.css";
import "../pages/giftCenter.css";

const tierOf = (coins = 0) =>
  coins >= 10000000 ? "mythic" : coins >= 5000 ? "legendary" : coins >= 1000 ? "epic" : coins >= 200 ? "rare" : "common";
const CATEGORIES = [
  { id: "small", label: "Small" },
  { id: "premium", label: "Premium" },
  { id: "luxury", label: "Luxury" },
  { id: "ultra", label: "Ultra" },
];

// Duration mapping for auto-close timeout
const ANIM_DURATIONS = { simple: 2800, medium: 3500, complex: 4500, epic: 6000 };

export const GiftPicker = ({ isOpen, onClose, recipientId, recipientName, photoId, onGiftSent }) => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState({});
  const [category, setCategory] = useState("small");
  const [selected, setSelected] = useState(null);
  const [balance, setBalance] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) { loadCatalog(); setSelected(null); setSent(null); }
  }, [isOpen]);

  const loadCatalog = async () => {
    setLoading(true);
    try {
      const [c, w] = await Promise.all([api.get("/gifts/catalog"), api.get("/wallet")]);
      setCatalog(c.data.categories || {});
      setBalance(w.data.balance || w.data.coin_balance || 0);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };

  const handleSend = async () => {
    if (!selected || sending) return;
    setSending(true);
    try {
      const body = { recipient_id: recipientId, gift_id: selected.id };
      if (photoId) body.photo_id = photoId;
      const res = await api.post("/gifts/send", body);
      setSent({ ...selected, ...res.data });
      setBalance(res.data.new_balance);
      onGiftSent?.(res.data);
      // Close picker immediately, animation plays independently
      setTimeout(() => onClose(), 300);
      // Clear sent after animation completes
      const dur = ANIM_DURATIONS[selected.animation || "simple"] || 2800;
      setTimeout(() => setSent(null), dur + 500);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore invio"); }
    finally { setSending(false); }
  };

  const formatCost = (n) => n >= 1000000 ? `${(n/1000000).toFixed(1)}M` : n >= 1000 ? `${(n/1000).toFixed(n >= 10000 ? 0 : 1)}K` : String(n);

  const gifts = isOpen ? (catalog[category] || []) : [];

  return createPortal(
    <>
    {/* Cinematic Gift Animation — persists independently of picker open/close */}
    <CinematicGiftAnimation
      gift={sent}
      isVisible={!!sent}
      onComplete={() => setSent(null)}
    />

    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-md z-[70]" onClick={onClose} />

          {/* === BOTTOM SHEET === */}
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-[70] rounded-t-[28px] max-h-[88vh] overflow-hidden gc-sheet"
            data-testid="gift-picker">
            <div className="gc-sheet-aurora" />
            <div className="relative flex justify-center py-3"><div className="w-10 h-1 rounded-full bg-white/25" /></div>

            {/* Header */}
            <div className="relative px-5 pb-3 flex items-center justify-between">
              <div>
                <h2 className="gc-title" style={{ fontSize: 22 }}>{t("giftPickerTitle")}</h2>
                <p className="gc-sub">a {recipientName || "utente"}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="gc-hero-coins" style={{ marginTop: 0 }}>
                  <AdeCoinIcon size={16} />
                  <span className="text-sm font-bold" data-testid="gift-picker-balance">{balance === null ? "…" : Math.floor(balance).toLocaleString()}</span>
                </div>
                <button onClick={() => { onClose(); navigate('/app/credits'); }}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold shadow-md hover:shadow-lg transition-all"
                  data-testid="gift-picker-buy-coins-btn">
                  <Plus className="w-3.5 h-3.5" />
                  <span>Acquista</span>
                </button>
                <button onClick={onClose} className="gc-back" data-testid="gift-picker-close"><X className="w-5 h-5" /></button>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="gc-tabs mx-5 mb-4" style={{ marginTop: 0 }}>
              {CATEGORIES.map(c => (
                <button key={c.id} onClick={() => { setCategory(c.id); setSelected(null); }}
                  data-testid={`gift-cat-${c.id}`}
                  className={`gc-tab ${category === c.id ? "gc-on" : ""}`}>
                  {category === c.id && <motion.span layoutId="gc-cat-glow" className="gc-tab-glow" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
                  <span className="relative">{c.label}</span>
                </button>
              ))}
            </div>

            {/* Gift Grid — IMAGE BASED */}
            <div className="px-5 overflow-y-auto" style={{ maxHeight: "40vh" }}>
              {loading ? (
                <div className="grid grid-cols-3 gap-3">{[1,2,3,4,5,6].map(i => <div key={i} className="h-32 rounded-2xl bg-white/5 border border-white/10 animate-pulse" />)}</div>
              ) : gifts.length === 0 ? (
                <div className="text-center py-12"><p className="text-white/40 text-sm">Nessun Gift disponibile</p></div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {gifts.map((g, i) => (
                    <motion.button key={g.id}
                      initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.03 }}
                      whileTap={{ scale: 0.93 }}
                      onClick={() => setSelected(g)}
                      data-testid={`gift-item-${g.id}`}
                      className="gc-pick"
                      style={{
                        "--gp-accent": getTier(tierOf(g.coin_cost)).accent,
                        "--gp-glow": getTier(tierOf(g.coin_cost)).glow,
                        borderColor: selected?.id === g.id ? getTier(tierOf(g.coin_cost)).accent : undefined,
                      }}>
                      <span className="gc-pick-aura" />
                      <div className="relative w-full aspect-square rounded-xl overflow-hidden mb-1.5 flex items-center justify-center">
                        <img src={g.image || `/images/gifts/${g.id}.png`} alt={g.name}
                          className="w-full h-full object-contain p-1"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                        <div className="hidden w-full h-full items-center justify-center text-white/40"><GiftIcon className="w-7 h-7" /></div>
                      </div>
                      <p className="relative text-[11px] font-semibold text-white/85 truncate w-full text-center leading-tight">{g.name}</p>
                      <p className="relative text-[10px] font-black" style={{ color: getTier(tierOf(g.coin_cost)).accent }}>{formatCost(g.coin_cost)}</p>
                      {balance !== null && balance < g.coin_cost && (
                        <div className="absolute inset-0 bg-black/65 rounded-2xl flex items-center justify-center pointer-events-none"
                          data-testid={`gift-insufficient-${g.id}`}>
                          <span className="text-[10px] text-white/60 font-bold">Insufficiente</span>
                        </div>
                      )}
                    </motion.button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Gift Preview + CTA */}
            <AnimatePresence>
              {selected && (
                <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                  className="relative px-5 py-4 border-t border-white/10 safe-bottom">
                  {/* Anteprima del reveal che vedrà il destinatario */}
                  <div className="gc-reveal-preview" data-testid="gift-reveal-preview"
                    style={{
                      "--unc-accent": getTier(tierOf(selected.coin_cost)).accent,
                      "--unc-accent2": getTier(tierOf(selected.coin_cost)).accent2,
                      "--unc-glow": getTier(tierOf(selected.coin_cost)).glow,
                    }}>
                    <div className="unc-cube-wrap" style={{ width: 40, height: 40 }}>
                      <div className="unc-cube" style={{ width: 40, height: 40 }}>
                        <i /><i /><i /><i /><i /><i />
                      </div>
                    </div>
                    <div>
                      <p className="gc-preview-label">
                        <Sparkles className="w-3 h-3 inline mr-1" />
                        Reveal {getTier(tierOf(selected.coin_cost)).label}
                      </p>
                      <p className="gc-preview-text">Arriverà sigillato: lo aprirà con l'animazione cinematica</p>
                    </div>
                  </div>

                  <div className="relative flex items-center gap-4 mt-3">
                    <div className="w-16 h-16 rounded-2xl overflow-hidden p-1 flex items-center justify-center"
                      style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${getTier(tierOf(selected.coin_cost)).accent}` }}>
                      <img src={selected.image || `/images/gifts/${selected.id}.png`} alt={selected.name} className="w-full h-full object-contain" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-white">{selected.name}</p>
                      <p className="text-sm" style={{ color: getTier(tierOf(selected.coin_cost)).accent }}>{selected.coin_cost.toLocaleString("it-IT")} ADE</p>
                      {balance === null ? (
                        <p className="text-xs text-white/40">Caricamento saldo…</p>
                      ) : balance >= selected.coin_cost ? (
                        <p className="text-xs text-white/40">Saldo dopo: {Math.max(0, Math.floor(balance - selected.coin_cost)).toLocaleString("it-IT")}</p>
                      ) : (
                        <p className="text-xs text-rose-400 font-semibold">Ti mancano {(selected.coin_cost - Math.floor(balance)).toLocaleString("it-IT")} crediti</p>
                      )}
                    </div>
                    {balance === null || balance >= selected.coin_cost ? (
                      <motion.button whileTap={{ scale: 0.95 }} onClick={handleSend}
                        disabled={sending} data-testid="send-gift-btn"
                        className="px-6 py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm shadow-lg shadow-amber-200 disabled:opacity-50 transition-all">
                        {sending ? t("giftSending") : t("giftSendBtn")}
                      </motion.button>
                    ) : (
                      <motion.button whileTap={{ scale: 0.95 }}
                        onClick={() => { onClose(); navigate('/app/credits'); }}
                        data-testid="send-gift-buy-btn"
                        className="px-5 py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm shadow-lg shadow-amber-200 transition-all flex items-center gap-1.5">
                        <Plus className="w-4 h-4" />
                        Acquista
                      </motion.button>
                    )}
                  </div>
                  <p className="relative text-[10px] text-white/35 text-center mt-2">Virtual Gift — Bene virtuale simbolico</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </>
      )}
    </AnimatePresence>
    </>,
    document.body
  );
};

export default GiftPicker;
